import { cn } from "@chams-pay/design-system/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function FilterSelect({
  value,
  options,
  className,
  ariaLabel,
  onChange,
}: {
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly className?: string;
  readonly ariaLabel?: string;
  readonly onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !rootRef.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "chams-focusable flex h-10 w-full items-center gap-2 rounded-md border border-input bg-card px-3 font-heading text-sm text-foreground",
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate text-start">{selected?.label}</span>
        <ChevronDown
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground transition-transform data-[open=true]:rotate-180"
          data-open={open}
        />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute inset-x-0 top-full z-[80] mt-1 w-full overflow-hidden rounded-md border border-border bg-popover p-1 font-heading shadow-e3"
        >
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className="chams-focusable flex min-h-9 w-full items-center gap-2 rounded-sm px-2.5 text-start text-sm text-foreground hover:bg-sunken"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {active && <Check aria-hidden="true" className="size-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
